import { extractFormData, parseHTMLSection } from "./CartUtils";
import CartAPI from "./CartAPI";
import MainCart from "./MainCart";

class SideCart extends HTMLElement {
    private cartAPI;
    maincart = document.querySelector<MainCart>('main-cart');

    constructor() {
        super();
        this.cartAPI = CartAPI;
        this.init();
    }

    /**
     * Initializes the side cart by setting up events.
     */
    private init(): void {
        this.setupEventListeners();
        console.info('SideCart initialized');
    }

    /**
     * Opens the side cart.
     * @param triggeredBy - The source that triggered the cart to open.
     */
    open(triggeredBy: string): void {
        const pageOverlay = document.querySelector<HTMLElement>('.page-overlay-sidecart')
        setTimeout(() => {
            this.classList.add('open');
            pageOverlay?.classList.add('open')
        });

        console.info('SideCart opened triggered by:', triggeredBy);
    }

    /**
     * Closes the side cart.
     * @param triggeredBy - The source that triggered the cart to close.
     */
    close(triggeredBy: string): void {
        const pageOverlay = document.querySelector<HTMLElement>('.page-overlay-sidecart')
        setTimeout(() => {
            this.classList.remove('open');
            pageOverlay?.classList.remove('open')
        });
        console.info('SideCart closed triggered by:', triggeredBy);
    }

    /**
     * Sets up event listeners for the side cart.
     */
    setupEventListeners(): void {
        this.initializeOpenEvent();
        this.initializeCloseEvent();
        this.initializeAddEvent();
        this.initializeRemoveEvent();
        this.initializeUpdateEvent();
        this.initializeUpdateVariantEvent();
    }

    private initializeOpenEvent(): void {
        const cartButton = document.querySelector<HTMLButtonElement | HTMLAnchorElement>('#side-cart-button');

        if (!cartButton) {
            console.error('The button/anchor with id #side-cart-button could not be found.');
        } else {
            cartButton.addEventListener('click', (event: Event) => {
                event.preventDefault(); // Prevents default behavior in case it's a link

                if (typeof this.open === 'function') {
                    this.open('button');
                } else {
                    console.error('The open method does not exist on the <side-cart> element.');
                }
            });
        }
    }


    private initializeCloseEvent(): void {
        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close("escape"));
        const closeButton = this.querySelector('#side-cart-close');
        if (closeButton) {
            closeButton.addEventListener('click', () => this.close("inner-close-button"));
        }
    }

    /**
     * Initializes the add-to-cart event.
     */
    public initializeAddEvent(): void {
        const buttons = document.querySelectorAll('.add-to-cart');
        buttons.forEach((button) => {
            button.addEventListener('click', async (evt) => {
                evt.preventDefault();

                console.info('Add to cart clicked');
                const formData = extractFormData(button as HTMLButtonElement);
                if (formData) {
                    try {
                        const result = await this.cartAPI.addToCart(formData);
                        console.info('Item added to cart:', result);
                        const sections = result['sections'];
                        const itemCount = result['item_count'];
                        this.renderHTML(sections);
                        this.maincart?.updateCart(itemCount)
                    } catch (error) {
                        console.error('Failed to add item to cart:', error);
                    }
                }
            });
        });
    }

    /**
     * Initializes the remove-from-cart event.
     */
    private initializeRemoveEvent(): void {
        const buttons = this.querySelectorAll('.side-cart-item__remove');
        buttons.forEach((button) => {
            button.addEventListener('click', async (evt) => {
                evt.preventDefault();
                console.info('Remove from cart clicked');
                const item = button.getAttribute('data-line-item');
                const formData = {
                    id: item,
                    quantity: 0,
                    sections: 'ajax-side-cart,ajax-cart-icon'
                };
                if (formData) {
                    try {
                        const result = await this.cartAPI.removeFromCart(formData);
                        console.info('Item removed from cart:', result);
                        const sections = result['sections'];
                        const itemCount = result['item_count'];
                        this.renderHTML(sections);
                        this.maincart?.updateCart(itemCount)
                    } catch (error) {
                        console.error('Failed to remove item from cart:', error);
                    }
                }
            });
        });
    }

    /**
     * Initializes the update cart item event.
     */
    private initializeUpdateEvent(): void {
        const inputs = this.querySelectorAll('.side-cart-item__quantity-input');
        inputs.forEach((input) => {
            const inputElement = input as HTMLInputElement;
            inputElement.addEventListener('change', async (evt) => {
                evt.preventDefault();
                console.info('Update cart item');
                const item = inputElement.getAttribute('data-line-item');
                const formData = {
                    id: item,
                    quantity: inputElement.value,
                    sections: 'ajax-side-cart,ajax-cart-icon'
                };
                if (formData) {
                    try {
                        const result = await this.cartAPI.updateItemCart(formData);
                        console.info('Cart item updated:', result);
                        const sections = result['sections'];
                        const itemCount = result['item_count'];
                        this.renderHTML(sections);
                        this.maincart?.updateCart(itemCount)
                    } catch (error) {
                        console.error('Failed to update cart item:', error);
                    }
                }
            });
        });
    }

    public initializeUpdateVariantEvent(): void {
        const inputs = document.querySelectorAll('.cart__item__variant__input');
        inputs.forEach((input) => {
            const inputElement = input as HTMLInputElement;
            inputElement.addEventListener('change', async (evt) => {
                evt.preventDefault();
                console.info('Update cart item');
                const item = inputElement.getAttribute('data-line-item');
                const styleNumber = inputElement.getAttribute('data-style')
           
                const formDataToDelete = {
                    id: item,
                    quantity: 0,
                    sections: 'ajax-side-cart,ajax-cart-icon'
                };

                const formDataNewItem = {
                    items: [
                        {
                            id: Number(inputElement.value),
                            quantity: 1,
                            properties: {
                                "Style": styleNumber
                            }
                        },
                    ],  
                    sections: 'ajax-side-cart,header'
                };
                if (formDataToDelete && formDataNewItem) {
                    try {
                        const resultDelete = await this.cartAPI.removeFromCart(formDataToDelete);
                        console.info('Item removed from cart:', resultDelete);

                        const resultNew = await this.cartAPI.addToCart(formDataNewItem);
                        console.info('Item added to cart:', resultNew);

                        const sections = resultNew['sections'];
                        this.renderHTML(sections);
                    } catch (error) {
                        console.error('Failed to remove item from cart:', error);
                    }
                }
            });
        });
    }

    /**
     * Called when the element is added to the DOM.
     */
    private renderHTML(sections: any): void {
        const HTMLSection = parseHTMLSection(sections['ajax-side-cart']);
        const sideCart = document.querySelector('side-cart');

        if (!sideCart) {
            console.error('No se encontró el elemento "side-cart".');
            return;
        }

        sideCart.innerHTML = '';
        sideCart?.appendChild(HTMLSection);

        
        this.initializeUpdateEvent();
        this.initializeUpdateVariantEvent();
        this.initializeRemoveEvent();
        this.initializeCloseEvent();
        this.initializeAddEvent();

        this.open("update");

        const newCartIcon = parseHTMLSection(sections['ajax-cart-icon']);
        const cartIcon = document.querySelector('#side-cart-button');

        if (!cartIcon) {
            console.error('No se encontró el elemento "cart-icon".');
            return;
        }

        cartIcon.innerHTML = '';
        cartIcon?.appendChild(newCartIcon);
        this.initializeOpenEvent();

        console.info('Cart updated.');
        return;
    }
}

export default SideCart;
